import React, { useState } from 'react';
import {
  TextField,
  Button,
  Paper,
  useTheme,
  IconButton,
  Tooltip,
  Box,
  CircularProgress,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Trash2, Wand2, Copy } from 'lucide-mui';
import { useTranslation } from 'react-i18next';
import { FeedbackSnackbar, useIframeResizer, GEMINI_MODEL } from './common';
import { config } from '../../../config';

interface AIHumanizerState {
  inputText: string;
  outputText: string;
  isLoading: boolean;
  error: string | null;
  showError: boolean;
  copySuccess: boolean;
}

const AIHumanizer: React.FC = () => {
  const { t } = useTranslation();
  useIframeResizer();
  const theme = useTheme();
  const [state, setState] = useState<AIHumanizerState>({
    inputText: '',
    outputText: '',
    isLoading: false,
    error: null,
    showError: false,
    copySuccess: false,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!state.inputText.trim() || state.isLoading) return;

    setState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await fetch(config.geminiProxyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: GEMINI_MODEL,
          contents: [
            {
              parts: [
                {
                  text: `Please make the following text sound more natural and human-like while keeping the same meaning. Feel free to use markdown formatting to improve readability: "${state.inputText}"`,
                },
              ],
            },
          ],
        }),
      });

      if (!response.ok) {
        throw new Error(t('aiHumanizer.messages.apiError'));
      }

      const data = await response.json();
      if (data.candidates && data.candidates[0]) {
        setState((prev) => ({
          ...prev,
          outputText: data.candidates[0].content.parts[0].text,
          isLoading: false,
        }));
      }
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: t('aiHumanizer.messages.error'),
        showError: true,
        isLoading: false,
      }));
    }
  };

  const handleClear = () => {
    setState((prev) => ({
      ...prev,
      inputText: '',
      outputText: '',
      error: null,
    }));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(state.outputText);
      setState((prev) => ({ ...prev, copySuccess: true }));
      setTimeout(() => {
        setState((prev) => ({ ...prev, copySuccess: false }));
      }, 2000);
    } catch (err) {
      setState((prev) => ({
        ...prev,
        error: t('aiHumanizer.messages.copyError'),
        showError: true,
      }));
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        minHeight: '100vh',
        width: '100%',
        p: { xs: 0, sm: 3 },
        position: 'relative',
        left: '50%',
        transform: 'translateX(-50%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          maxWidth: { xs: '100%', sm: 600 },
          minHeight: { xs: '100vh', sm: 'auto' },
          p: { xs: 2, sm: 3 },
          borderRadius: { xs: 0, sm: 2 },
        }}
      >
        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
          <TextField
            fullWidth
            multiline
            minRows={4}
            maxRows={8}
            value={state.inputText}
            onChange={(e) =>
              setState((prev) => ({ ...prev, inputText: e.target.value }))
            }
            placeholder={t('aiHumanizer.placeholders.input')}
            variant="outlined"
            disabled={state.isLoading}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Button
              type="submit"
              variant="contained"
              disabled={state.isLoading || !state.inputText.trim()}
              startIcon={
                state.isLoading ? <CircularProgress size={20} /> : <Wand2 />
              }
            >
              {state.isLoading
                ? t('aiHumanizer.buttons.processing')
                : t('aiHumanizer.buttons.humanize')}
            </Button>

            {state.inputText && (
              <Tooltip title={t('aiHumanizer.tooltips.clear')}>
                <IconButton
                  onClick={handleClear}
                  disabled={state.isLoading}
                  size="large"
                >
                  <Trash2 />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </form>

        {state.outputText && (
          <Box sx={{ mt: 2 }}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                position: 'relative',
                backgroundColor: theme.palette.background.default,
              }}
            >
              <Box
                sx={{
                  fontFamily: 'Inter',
                  '& p': { m: 0 },
                  '& p:not(:last-child)': { mb: 1 },
                  '& code': {
                    p: 0.5,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    fontFamily: 'monospace',
                  },
                  '& pre': {
                    p: 1,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    overflow: 'auto',
                    '& code': {
                      p: 0,
                      bgcolor: 'transparent',
                    },
                  },
                  '& ul, & ol': {
                    m: 0,
                    pl: 2.5,
                  },
                  '& li': {
                    mb: 0.5,
                  },
                  '& a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  },
                  '& table': {
                    borderCollapse: 'collapse',
                    width: '100%',
                  },
                  '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    p: 1,
                  },
                  '& blockquote': {
                    m: 0,
                    pl: 2,
                    borderLeft: '4px solid',
                    borderColor: 'divider',
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {state.outputText}
                </ReactMarkdown>
              </Box>
              <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                <Tooltip
                  title={
                    state.copySuccess
                      ? t('aiHumanizer.tooltips.copied')
                      : t('aiHumanizer.tooltips.copy')
                  }
                >
                  <IconButton onClick={handleCopy} size="small">
                    <Copy />
                  </IconButton>
                </Tooltip>
              </Box>
            </Paper>
          </Box>
        )}

        <FeedbackSnackbar
          open={!!state.error && state.showError}
          message={state.error || t('aiHumanizer.messages.error')}
          severity="error"
          onClose={() => setState((prev) => ({ ...prev, showError: false }))}
        />
      </Paper>
    </Box>
  );
};

export default AIHumanizer;
